import { c as _c } from "react/compiler-runtime";
import { useLayoutEffect, useMemo, useRef } from 'react';
import { styled } from '@classify/react-core-components/styles';
import { useParams } from 'next/navigation';
import { translate } from '@openclassrooms/translations';
import Typography from '@classify/react-core-components/Typography';
import ContentWrapper from '@classify/react-core-components/ContentWrapper';
import type { TableOfContent as TableOfContentType } from '@openclassrooms/sdk-web';
import { useChapterContext } from '../_contexts/ChapterContext';
import PartTitle from './PartTitle';
import ChapterTocItem from '../../_components/TableOfContent/ChapterTocItem';
import QuizTocItem from '../../_components/TableOfContent/QuizTocItem';
import TocHeader from './TocHeader';
const TocRoot = styled('div', {
  shouldForwardProp: prop => prop !== 'variant'
})<{
  variant?: TocVariants;
}>(({
  variant
}) => ({
  height: '100%',
  overflow: 'scroll',
  overflowX: 'hidden',
  ...(variant === TocVariants.HOMEPAGE && {
    overflow: 'hidden'
  })
}));
const PartsList = styled('ul')({
  margin: 0,
  padding: 0,
  listStyle: 'none'
});
const ChapterList = styled('ul')(({
  theme: {
    spacing,
    palette
  }
}) => ({
  margin: spacing(1, 2, 2, 2),
  padding: 0,
  '& > *': {
    position: 'relative',
    '&::before, &::after': {
      position: 'absolute',
      left: 19,
      height: 8,
      content: '" "',
      borderLeft: `1px solid ${palette.line}`
    },
    '&::before': {
      top: 0,
      height: 4
    },
    '&::after': {
      top: 44,
      height: 'calc(100% - 40px)'
    },
    '&:first-of-type::before': {
      top: -8,
      height: 16
    },
    '&:last-child::after': {
      bottom: -16,
      height: 'calc(100% - 28px)'
    }
  }
}));
const PartItem = styled('li', {
  shouldForwardProp: prop => prop !== 'isLastPart'
})<{
  isLastPart?: boolean;
}>(({
  isLastPart
}) => ({
  ...(isLastPart && {
    '& > *:last-child::after': {
      display: 'none'
    }
  })
}));
const TocTitle = styled(Typography)(({
  theme: {
    spacing,
    palette,
    breakpoints
  }
}) => ({
  borderBottom: `1px solid ${palette.line}`,
  paddingBottom: spacing(1),
  [breakpoints.up('sm')]: {
    marginBottom: spacing(3)
  }
}));
export enum TocVariants {
  CHAPTER = 'CHAPTER',
  HOMEPAGE = 'HOMEPAGE',
}
export interface TableOfContentsProps {
  color: string;
  onClose?: () => void;
  onClickItem?: () => void;
  variant?: TocVariants;
  sectionComponent?: any;
}
function getCurrentChapter(flatToc: any[], id: number | undefined) {
  if (!id) return null;
  return flatToc.find(item => item.id === id);
}
const TableOfContents = t0 => {
  const $ = _c(38);
  const {
    color,
    onClose: t1,
    onClickItem: t2,
    variant: t3,
    sectionComponent
  } = t0;
  const onClose = t1 === undefined ? _temp : t1;
  const onClickItem = t2 === undefined ? _temp2 : t2;
  const variant = t3 === undefined ? TocVariants.CHAPTER : t3;
  const {
    course,
    tableOfContent: userToc,
    flatToc,
    currentChapter: currentChapterFromContext
  } = useChapterContext();
  const currentChapterId = currentChapterFromContext?.id;
  const exerciseId = useParams();
  const tocRef = useRef(null);
  let t4;
  if ($[0] !== userToc?.children) {
    t4 = userToc?.children ?? [];
    $[0] = userToc?.children;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const parts = t4;
  let t5;
  if ($[2] !== currentChapterId || $[3] !== flatToc) {
    t5 = () => {
      const currentChapter = getCurrentChapter(flatToc, currentChapterId);
      if (currentChapter && currentChapter.parentId) {
        const partId = currentChapter.parentId;
        const selectedPart = document.querySelector(`#part-${partId}`) as HTMLElement;
        if (tocRef.current && selectedPart) {
          tocRef.current.scrollTop = selectedPart.offsetTop - 75;
        }
      }
    };
    $[2] = currentChapterId;
    $[3] = flatToc;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== currentChapterId || $[6] !== exerciseId || $[7] !== flatToc || $[8] !== parts) {
    t6 = [exerciseId, parts, currentChapterId, flatToc];
    $[5] = currentChapterId;
    $[6] = exerciseId;
    $[7] = flatToc;
    $[8] = parts;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  useLayoutEffect(t5, t6);
  let t7;
  if ($[10] !== onClose || $[11] !== variant) {
    t7 = variant === TocVariants.CHAPTER && <TocHeader onClose={onClose} />;
    $[10] = onClose;
    $[11] = variant;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== variant) {
    t8 = variant === TocVariants.HOMEPAGE && <ContentWrapper><TocTitle variant="h3" component="h2">{translate("course_home.toc.title")}</TocTitle></ContentWrapper>;
    $[13] = variant;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== color || $[16] !== course || $[17] !== currentChapterId || $[18] !== onClickItem || $[19] !== parts || $[20] !== sectionComponent || $[21] !== variant) {
    let t10;
    if ($[23] !== color || $[24] !== course || $[25] !== currentChapterId || $[26] !== onClickItem || $[27] !== parts.length || $[28] !== sectionComponent || $[29] !== variant) {
      t10 = (part, index) => {
        const isLastPart = index === parts.length - 1;
        return <PartItem key={part.id} isLastPart={isLastPart}><PartTitle number={index + 1} color={color} component={sectionComponent} id={part.id.toString()}>{part.title}</PartTitle><ChapterList>{((part as any).children ?? []).map((subPart, subIndex) => {
              if (subPart.type === "MCQ") {
                return <QuizTocItem key={subPart.id} quizPart={subPart} isOnHomePage={variant === TocVariants.HOMEPAGE} onClick={onClickItem} categoryColor={color} course={course} />;
              }
              return <ChapterTocItem key={subPart.id} number={subIndex + 1} chapterPart={subPart} isOnHomePage={variant === TocVariants.HOMEPAGE} onClick={onClickItem} categoryColor={color} currentChapterId={Number(currentChapterId)} course={course} />;
            })}</ChapterList></PartItem>;
      };
      $[23] = color;
      $[24] = course;
      $[25] = currentChapterId;
      $[26] = onClickItem;
      $[27] = parts.length;
      $[28] = sectionComponent;
      $[29] = variant;
      $[30] = t10;
    } else {
      t10 = $[30];
    }
    t9 = parts.map(t10);
    $[15] = color;
    $[16] = course;
    $[17] = currentChapterId;
    $[18] = onClickItem;
    $[19] = parts;
    $[20] = sectionComponent;
    $[21] = variant;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[31] !== t9) {
    t10 = <PartsList>{t9}</PartsList>;
    $[31] = t9;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== t10 || $[34] !== t7 || $[35] !== t8 || $[36] !== variant) {
    t11 = <TocRoot ref={tocRef} variant={variant}>{t7}{t8}{t10}</TocRoot>;
    $[33] = t10;
    $[34] = t7;
    $[35] = t8;
    $[36] = variant;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  return t11;
};
export default TableOfContents;
function _temp() {}
function _temp2() {}